import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddLinkIcon from '@mui/icons-material/AddLink';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import licenseComplianceService from '../../services/licenseComplianceService';
import type { SubjectRequirementFulfillmentDto } from '../../services/licenseComplianceService';
import cpeHistoryService from '../../services/cpeHistoryService';
import type { CreditRecord } from '../../services/cpeHistoryService';
import { getApiErrorInfo } from '../../services/api';
import { formatUtcDate } from '../../utils/dateTime';
import { EASE_OUT_QUART, SubGoalFlash, chipPop } from './ComplianceCelebration';

interface ManualRequirementTileProps {
  licenseId: string;
  requirement: SubjectRequirementFulfillmentDto;
  /** When true, renders the interactive check-off and link picker (member's own view). */
  interactive?: boolean;
  /** Called after a successful change so the parent can refresh fulfillment. */
  onChanged?: () => void | Promise<void>;
  /** Fires the small sub-goal celebration (tile flash + chip pop). Caller gates reduced motion. */
  justMet?: boolean;
}

/** One linkable credit in the picker: a platform registration or a third-party activity. */
interface LinkableCredit {
  key: string;
  registrationId?: string;
  thirdPartyCreditGroupId?: string;
  title: string;
  dateEarned: string;
  hours: number;
  external: boolean;
}

/**
 * Collapses raw credit rows into linkable activities: platform rows keyed by
 * registration, third-party rows keyed by credit group (one activity can span
 * several category rows — hours are summed).
 */
function toLinkableCredits(records: CreditRecord[]): LinkableCredit[] {
  const byKey = new Map<string, LinkableCredit>();
  for (const record of records) {
    const external = record.source === 'External';
    const id = external ? record.creditGroupId : record.registrationId;
    if (!id) continue;
    const key = `${external ? 'g' : 'r'}:${id}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.hours += record.creditsEarned;
    } else {
      byKey.set(key, {
        key,
        registrationId: external ? undefined : id,
        thirdPartyCreditGroupId: external ? id : undefined,
        title: record.courseTitle ?? 'Untitled activity',
        dateEarned: record.dateEarned,
        hours: record.creditsEarned,
        external,
      });
    }
  }
  return [...byKey.values()].sort(
    (a, b) => new Date(b.dateEarned).getTime() - new Date(a.dateEarned).getTime()
  );
}

/**
 * Fulfillment tile for a manual (attested) subject requirement: the check-off
 * is the sole fulfillment authority; linked credits are optional evidence.
 * Read-only unless `interactive` (admin drawer reuses it without interaction).
 */
export default function ManualRequirementTile({
  licenseId,
  requirement,
  interactive = false,
  onChanged,
  justMet = false,
}: ManualRequirementTileProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Link picker state
  const [credits, setCredits] = useState<LinkableCredit[] | null>(null);
  const [creditsError, setCreditsError] = useState<string | null>(null);
  const [linkingKey, setLinkingKey] = useState<string | null>(null);

  const fulfilled = !!requirement.fulfilledAt;
  const evidence = requirement.evidence ?? [];

  useEffect(() => {
    if (!pickerOpen) return;
    setCreditsError(null);
    cpeHistoryService
      .getUserCredits()
      .then((records) => setCredits(toLinkableCredits(records)))
      .catch((err) => setCreditsError(getApiErrorInfo(err).message));
  }, [pickerOpen]);

  const linkedRegistrationIds = useMemo(
    () => new Set(evidence.map((e) => e.registrationId).filter(Boolean)),
    [evidence]
  );
  const linkedGroupIds = useMemo(
    () => new Set(evidence.map((e) => e.thirdPartyCreditGroupId).filter(Boolean)),
    [evidence]
  );

  async function handleToggle(checked: boolean) {
    setSaving(true);
    setError(null);
    try {
      await licenseComplianceService.setSubjectRequirementFulfillment(
        licenseId,
        requirement.id,
        checked
      );
      await onChanged?.();
    } catch (err) {
      setError(getApiErrorInfo(err).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleLink(credit: LinkableCredit) {
    setLinkingKey(credit.key);
    setError(null);
    try {
      await licenseComplianceService.addSubjectRequirementEvidence(licenseId, requirement.id, {
        registrationId: credit.registrationId ?? null,
        thirdPartyCreditGroupId: credit.thirdPartyCreditGroupId ?? null,
      });
      await onChanged?.();
    } catch (err) {
      setError(getApiErrorInfo(err).message);
    } finally {
      setLinkingKey(null);
    }
  }

  async function handleUnlink(evidenceId: string) {
    setSaving(true);
    setError(null);
    try {
      await licenseComplianceService.removeSubjectRequirementEvidence(
        licenseId,
        requirement.id,
        evidenceId
      );
      await onChanged?.();
    } catch (err) {
      setError(getApiErrorInfo(err).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {justMet && <SubGoalFlash />}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {requirement.name}
        </Typography>
        {/* Exception-only signaling: the checkbox below already carries the
            unfulfilled state, so only done gets a mark — a check glyph, not a
            chip (matches the subject tiles). */}
        {fulfilled && (
          <CheckCircleRoundedIcon
            titleAccess="Fulfilled"
            sx={{
              fontSize: 16,
              color: 'status.completed.fg',
              animation: justMet ? `${chipPop} 0.45s ${EASE_OUT_QUART} both 0.2s` : undefined,
            }}
          />
        )}
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
        Attested requirement: you confirm completion yourself
      </Typography>

      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={fulfilled}
            disabled={!interactive || saving}
            onChange={(e) => void handleToggle(e.target.checked)}
          />
        }
        label={
          <Typography variant="body2">
            {fulfilled && requirement.fulfilledAt
              ? `Fulfilled ${formatUtcDate(requirement.fulfilledAt)}`
              : 'Mark as fulfilled for this period'}
          </Typography>
        }
        sx={{ ml: -0.75, mr: 0 }}
      />

      {(evidence.length > 0 || interactive) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
          {evidence.map((item) => (
            <Tooltip
              key={item.id}
              title={
                item.dateEarned
                  ? `${item.hours?.toFixed(1) ?? '—'} hrs · ${formatUtcDate(item.dateEarned)}`
                  : ''
              }
            >
              <Chip
                label={item.title}
                size="small"
                variant="outlined"
                icon={item.thirdPartyCreditGroupId ? <OpenInNewIcon /> : undefined}
                onDelete={interactive && !saving ? () => void handleUnlink(item.id) : undefined}
                sx={{ maxWidth: 220 }}
              />
            </Tooltip>
          ))}
          {interactive && (
            <Button
              size="small"
              startIcon={<AddLinkIcon />}
              onClick={() => setPickerOpen(true)}
              disabled={saving}
            >
              Link course
            </Button>
          )}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Evidence link picker over the user's combined CPE history */}
      <Dialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Link a course to “{requirement.name}”</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', px: 2.5, pt: 1.5 }}>
            Linked courses are supporting evidence: they don't change your hour totals, and the
            check-off above stays in your control.
          </Typography>
          {creditsError ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {creditsError}
            </Alert>
          ) : credits === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : credits.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', p: 2.5 }}>
              No credits in your CPE history yet. Complete a course or add a third-party credit
              first, then link it here.
            </Typography>
          ) : (
            <List dense>
              {credits.map((credit) => {
                const linked = credit.registrationId
                  ? linkedRegistrationIds.has(credit.registrationId)
                  : linkedGroupIds.has(credit.thirdPartyCreditGroupId);
                return (
                  <ListItem
                    key={credit.key}
                    secondaryAction={
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={linked || linkingKey !== null}
                        onClick={() => void handleLink(credit)}
                        startIcon={
                          linkingKey === credit.key ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : undefined
                        }
                      >
                        {linked ? 'Linked' : 'Link'}
                      </Button>
                    }
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {credit.external && (
                            <Tooltip title="Third-party credit">
                              <OpenInNewIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                            </Tooltip>
                          )}
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {credit.title}
                          </Typography>
                        </Box>
                      }
                      secondary={`${credit.hours.toFixed(1)} hrs · ${formatUtcDate(credit.dateEarned)}`}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
